import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  CircularProgress,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  AlertTriangle,
  CheckCircle,
  Download,
  Eye,
  TrendingUp,
  TrendingDown,
  X,
  Clock,
  MapPin,
  Upload,
  FileSearch,
  FileText,
  CheckCircle2,
} from "lucide-react";
import axiosInstance from "../api/axiosInstance";

const getCurrentQuarterKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `q${quarter}_${year}`;
};

const generateQuarterOptions = () => {
  const options = [];
  const now = new Date();

  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;

  const labels = {
    1: "Jan-Mar",
    2: "Apr-Jun",
    3: "Jul-Sep",
    4: "Oct-Dec",
  };

  for (let y = currentYear; y >= currentYear - 2; y--) {
    const maxQuarter = y === currentYear ? currentQuarter : 4;

    for (let q = maxQuarter; q >= 1; q--) {
      options.push({
        value: `q${q}_${y}`,
        label: `Q${q} ${y} (${labels[q]})`,
      });
    }
  }

  return options;
};

const getTrimesterDates = (trimesterStr) => {
  if (!trimesterStr) return { start: "", end: "" };
  const [q, yearStr] = trimesterStr.split("_");
  const year = parseInt(yearStr);

  const quarters = {
    q1: { start: "01", end: "03", lastDay: "31" },
    q2: { start: "04", end: "06", lastDay: "30" },
    q3: { start: "07", end: "09", lastDay: "30" },
    q4: { start: "10", end: "12", lastDay: "31" },
  };

  const { start, end, lastDay } = quarters[q.toLowerCase()] || quarters.q1;

  return {
    start: `${year}-${start}-01`,
    end: `${year}-${end}-${lastDay}`,
  };
};

const formatDateShort = (iso) => {
  if (!iso || iso === "NO") return "";
  try {
    const dt = new Date(iso);
    return Number.isNaN(dt.getTime()) ? iso : dt.toLocaleDateString();
  } catch {
    return iso;
  }
};

const getProgressColor = (progress) => {
  if (progress >= 90) return "#10B981";
  if (progress >= 70) return "#3B82F6";
  if (progress >= 40) return "#F59E0B";
  return "#EF4444";
};

const transformDistrictData = (apiResponse) => {
  const dataList = apiResponse?.content || [];
  return dataList.map((item) => {
    const total = item.packageCount || 0;
    const completed = item.criteriaCount || item.analysis || 0;
    const uploaded = item.videoCount || 0;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      id: item.siteId,
      name: item.siteName || "Unknown",
      progress,
      color: getProgressColor(progress),
      total,
      active: uploaded,
      completed,
    };
  });
};

const transformReportData = (apiResponse) => {
  const dataList = apiResponse?.content || [];
  return dataList.map((item, index) => {
    const hasVideo = item.videoUploaded && item.videoUploaded !== "NO";
    const hasAnalysis = item.analysis && item.analysis !== "NO";

    return {
      id: item.packageName || `PKG-${index}`,
      packageId: item.packageId,
      district: item.siteName,
      districtCode: item.siteId,
      name: item.summary || "Package Construction",

      videoStatus: hasVideo ? "success" : "missing",
      videoDate: hasVideo ? item.videoUploaded : null,

      analysisStatus: hasAnalysis ? "success" : "pending",
      analysisDate: null,

      reportStatus: item.report ? "available" : "pending",
      reportUrl: item.report,
      defects: item.defects ? parseInt(item.defects) : null,
      summary: item.summary || "No summary available",

      action: !hasVideo ? "upload" : item.report ? "view" : "none",
    };
  });
};

const StatusIcon = ({ status, date }) => {
  const s = (status || "").toLowerCase();

  if (s.includes("missing")) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <AlertTriangle size={18} color="#EF4444" />
        <Typography
          variant="caption"
          sx={{ color: "#EF4444", mt: 0.5, fontWeight: 600 }}
        >
          Missing
        </Typography>
      </Box>
    );
  }
  if (s.includes("success")) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <CheckCircle size={18} color="#16A34A" />
        <Typography
          variant="caption"
          sx={{ color: "#6B7280", mt: 0.5, fontSize: "0.72rem" }}
        >
          {date && date !== "YES" ? formatDateShort(date) : "Done"}
        </Typography>
      </Box>
    );
  }
  // Default Pending
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Clock size={16} color="#9CA3AF" />
      <Typography variant="caption" sx={{ color: "#9CA3AF", mt: 0.5 }}>
        Pending
      </Typography>
    </Box>
  );
};

const DistrictCard = ({ data, selected, onClick }) => (
  <Paper
    elevation={selected ? 4 : 0}
    onClick={onClick}
    sx={{
      p: 2,
      width: "100%",
      height: "100%",
      borderRadius: 2,
      border: "1px solid",
      borderColor: selected ? "#3B82F6" : "#E5E7EB",
      bgcolor: "#f9fafb",
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.2s ease-in-out",
      "&:hover": { transform: "translateY(-4px)", boxShadow: 3 },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
        {data.name}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.4,
          color: data.progress > 0 ? data.color : "#EF4444",
          fontSize: "0.75rem",
          fontWeight: 600,
        }}
      >
        {data.progress > 0 ? (
          <TrendingUp size={12} />
        ) : (
          <TrendingDown size={12} />
        )}
        {data.progress}%
      </Box>
    </Box>
    <LinearProgress
      variant="determinate"
      value={data.progress}
      sx={{
        height: 5,
        borderRadius: 3,
        bgcolor: "#e5e7eb",
        "& .MuiLinearProgress-bar": { bgcolor: data.color },
        mb: 1.5,
      }}
    />
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        textAlign: "center",
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", ml: 1.3 }}>
          {data.total}
        </Typography>
        <Typography
          sx={{
            color: "#6B7280",
            fontSize: ".75rem",
            fontWeight: 600,
            ml: 1.3,
          }}
        >
          Packages
        </Typography>
      </Box>

      <Box>
        <Typography
          sx={{ fontWeight: 600, fontSize: "0.75rem", ml: 1.3, mr: 1.3 }}
        >
          {data.active}
        </Typography>
        <Typography
          sx={{
            color: "#6B7280",
            fontSize: "0.75rem",
            ml: 1.3,
            mr: 1.3,
            fontWeight: 600,
          }}
        >
          Video Uploaded
        </Typography>
      </Box>

      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", mr: 1.3 }}>
          {data.completed}
        </Typography>
        <Typography
          sx={{
            color: "#6B7280",
            fontSize: "0.75rem",
            mr: 1.3,
            fontWeight: 600,
          }}
        >
          Analysis Done
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const ReportDetailModal = ({ open, onClose, data }) => {
  if (!data) return null;
  const isComplete = data.reportStatus === "available";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Analysis Report - {data.id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
              Package Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Package ID
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {data.id}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  District
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {data.district}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
              Analysis Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Defects Found
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color:
                      data.defects && data.defects > 0 ? "#DC2626" : "#16A34A",
                  }}
                >
                  {data.defects !== null
                    ? data.defects === 0
                      ? "No Defects"
                      : `${data.defects} Issues`
                    : "--"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Summary
        </Typography>
        <Paper
          elevation={0}
          sx={{ p: 2, bgcolor: "#F9FAFB", border: "1px solid #E5E7EB", mb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            {data.summary}
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<Download size={16} />}
          disabled={!isComplete}
          sx={{ bgcolor: "#2563EB", fontWeight: 600 }}
        >
          Download Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function ReportPage() {
  const [trimester, setTrimester] = useState(getCurrentQuarterKey());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [districts, setDistricts] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState(null);
  const ROWS_PER_PAGE = 10;
  const quarterOptions = useMemo(() => generateQuarterOptions(), []);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchDistricts = async () => {
      setLoading(true);
      setError(null);

      try {
        const { start, end } = getTrimesterDates(trimester);

        const res = await axiosInstance.post(
          `/api/pre-check/video-report?start=${start}&end=${end}`,
          { page: 0, size: 100 }
        );

        if (!isMounted) return;

        const baseDistricts = transformDistrictData(res.data);

        const enrichedDistricts = await Promise.all(
          baseDistricts.map(async (district) => {
            try {
              const pkgRes = await axiosInstance.post(
                `/api/pre-check/package-report?siteId=${district.id}`,
                { page: 0, size: 100 }
              );

              const rows = pkgRes.data?.content || [];

              const total = rows.length;
              const videoUploaded = rows.filter(
                (r) => r.videoUploaded === "YES"
              ).length;
              const analysisDone = rows.filter(
                (r) => r.analysis === "YES"
              ).length;

              const progress =
                total > 0 ? Math.round((analysisDone / total) * 100) : 0;

              return {
                ...district,
                total,
                active: videoUploaded,
                completed: analysisDone,
                progress,
                color: getProgressColor(progress),
              };
            } catch {
              return district;
            }
          })
        );

        setDistricts(enrichedDistricts);
        setSelectedDistrict(null);
        setReports([]);
      } catch (err) {
        console.error(err);
        setError("Failed to load district reports");
      } finally {
        setLoading(false);
      }
    };

    fetchDistricts();

    return () => {
      isMounted = false;
    };
  }, [trimester]);

  useEffect(() => {
    let isMounted = true;

    const fetchPackages = async () => {
      if (!selectedDistrict) return;

      try {
        const res = await axiosInstance.post(
          `/api/pre-check/package-report?siteId=${selectedDistrict.id}`,
          {
            page: 0,
            size: 100,
          }
        );

        if (!isMounted) return;

        const rows = res.data?.content || [];
        const transformed = transformReportData(res.data);

        setReports(transformed);
        setPage(1);

        const total = rows.length;
        const videoUploaded = rows.filter(
          (r) => r.videoUploaded === "YES"
        ).length;
        const analysisDone = rows.filter((r) => r.analysis === "YES").length;

        const progress =
          total > 0 ? Math.round((analysisDone / total) * 100) : 0;

        setDistricts((prev) =>
          prev.map((d) =>
            d.id === selectedDistrict.id
              ? {
                  ...d,
                  total,
                  active: videoUploaded,
                  completed: analysisDone,
                  progress,
                  color: getProgressColor(progress),
                }
              : d
          )
        );
      } catch (err) {
        console.error("Package API Error:", err);
      }
    };

    fetchPackages();

    return () => {
      isMounted = false;
    };
  }, [selectedDistrict]);

  const totalPages = Math.ceil(reports.length / ROWS_PER_PAGE);
  const paginatedReports = reports.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  const handleViewReport = (report) => {
    setSelectedReportData(report);
    setModalOpen(true);
  };

  const handleDownloadDirect = (report) => {
    if (report.reportUrl) {
      window.open(report.reportUrl, "_blank");
    } else {
      alert("Report document is not available for this package yet.");
    }
  };

  return (
    <Box sx={{ bgcolor: "#F8FAFC", minHeight: "100vh", pb: 6 }}>
      <Container maxWidth={false} disableGutters sx={{ px: 3, pt: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <ArrowLeft size={16} sx={{ color: "#4b5563", fontWeight: 600 }} />
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: "#616a77" }}
            >
              Back to Dashboard
            </Typography>
          </Link>

          <Box sx={{ flexGrow: 1 }} />

          {/* RIGHT: Title + Trimester */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              whiteSpace: "nowrap",
            }}
          >
            {/* Title */}
            <Typography variant="h7" sx={{ fontWeight: 700, color: "#1F2937" }}>
              Post Construction Analysis Reports
            </Typography>

            {/* Trimester pill */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                bgcolor: "#F3F8FF",
                border: "1px solid #BFD6FF",
                borderRadius: 1.5,
                px: 2,
                py: 1,
              }}
            >
              {/* Calendar + Text */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarDays size={16} color="#2563EB" />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1D4ED8" }}
                >
                  Trimester:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "#1D4ED8",
                    whiteSpace: "nowrap",
                  }}
                >
                  {quarterOptions.find((o) => o.value === trimester)?.label ||
                    trimester}
                </Typography>
              </Box>

              <Select
                value={trimester}
                onChange={(e) => setTrimester(e.target.value)}
                size="small"
                sx={{
                  bgcolor: "white",
                  height: 32,
                  minWidth: 180,
                  borderRadius: 1.5,
                }}
              >
                {quarterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "40vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              {selectedDistrict && (
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "#EFF6FF",
                    border: "1px solid #3B82F6",
                    borderRadius: 1,
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MapPin size={16} color="#1f40af" />
                    <Typography
                      variant="body2"
                      sx={{ color: "#1f40af", fontWeight: 700 }}
                    >
                      Showing reports for:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "#1E40AF", fontWeight: 700 }}
                    >
                      {selectedDistrict.name}
                    </Typography>
                  </Box>

                  <Tooltip title="Clear selection">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedDistrict(null);
                        setReports([]);
                      }}
                      sx={{ color: "#3B82F6" }}
                    >
                      <X size={18} />
                    </IconButton>
                  </Tooltip>
                </Paper>
              )}
            </Box>

            <Paper
              elevation={0}
              sx={{
                bgcolor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: 2,
                mb: 4,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  bgcolor: "#F9FAFB",
                  borderBottom: "1px solid #E5E7EB",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "#111827" }}
                >
                  District Performance Summary
                </Typography>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: 2.5,
                  }}
                >
                  {districts.map((district) => (
                    <DistrictCard
                      key={district.id}
                      data={district}
                      selected={selectedDistrict?.id === district.id}
                      onClick={() => {
                        setSelectedDistrict(district);
                        setPage(1);
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Paper>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 2, color: "#4B5563" }}
            >
              Detailed Reports{" "}
              {selectedDistrict ? ` — ${selectedDistrict.name}` : ""}
            </Typography>

            <Paper
              elevation={0}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                    <TableRow>
                      {[
                        "District",
                        "Package",
                        "Video Uploaded",
                        "Analysis",
                        "Report",
                        "Road Condition",
                        "Actions",
                      ].map((head) => (
                        <TableCell
                          key={head}
                          sx={{
                            fontWeight: 600,
                            color: "#6B7280",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                          }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedReports.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#111827"
                          >
                            {row.district}
                          </Typography>
                          <Typography variant="caption" color="#6B7280">
                            {row.districtCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="#2563EB"
                          >
                            {row.id}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="#374151"
                            fontWeight={500}
                          >
                            {row.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pl: 6 }}>
                          <StatusIcon
                            status={row.videoStatus}
                            date={row.videoDate}
                          />
                        </TableCell>
                        <TableCell sx={{ pl: 4 }}>
                          <StatusIcon status={row.analysisStatus} />
                        </TableCell>
                        <TableCell sx={{ pl: 4 }}>
                          <StatusIcon status={row.reportStatus} />
                        </TableCell>
                        <TableCell>
                          {row.defects !== null ? (
                            <Chip
                              label={
                                row.defects === 0
                                  ? "Good"
                                  : `${row.defects} Issues`
                              }
                              size="small"
                              sx={{
                                bgcolor:
                                  row.defects === 0 ? "#ECFDF5" : "#FEF3C7",
                                color:
                                  row.defects === 0 ? "#065F46" : "#B45309",
                                fontWeight: 700,
                              }}
                            />
                          ) : (
                            <Chip label="N/A" size="small" />
                          )}
                        </TableCell>

                        <TableCell>
                          {row.videoStatus !== "success" && (
                            <Tooltip title="Upload Video">
                              <Button
                                component={Link}
                                to="/upload"
                                size="small"
                                variant="outlined"
                                startIcon={<Upload size={14} />}
                                sx={{ fontWeight: 600 }}
                              >
                                Upload Video
                              </Button>
                            </Tooltip>
                          )}

                          {row.videoStatus === "success" &&
                            row.analysisStatus !== "success" && (
                              <Tooltip title="Run AI Analysis">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={<FileSearch size={14} />}
                                  sx={{ fontWeight: 600 }}
                                >
                                  AI Analysis
                                </Button>
                              </Tooltip>
                            )}

                          {row.videoStatus === "success" &&
                            row.analysisStatus === "success" &&
                            row.reportStatus !== "available" && (
                              <Tooltip title="Generate Report">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<FileText size={14} />}
                                  sx={{ fontWeight: 600 }}
                                >
                                  Generate Report
                                </Button>
                              </Tooltip>
                            )}

                          {row.videoStatus === "success" &&
                            row.analysisStatus === "success" &&
                            row.reportStatus === "available" && (
                              <Tooltip title="Completed">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <CheckCircle2 size={18} color="#16a34a" />
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#16a34a", fontWeight: 700 }}
                                  >
                                    Completed
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {paginatedReports.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align="center"
                          sx={{ py: 6, color: "#6B7280" }}
                        >
                          {selectedDistrict
                            ? "No packages found."
                            : "Select a district above to view detailed reports."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: "1px solid #E5E7EB",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Showing {paginatedReports.length} results
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  shape="rounded"
                  size="small"
                  color="primary"
                />
              </Box>
            </Paper>

            <ReportDetailModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              data={selectedReportData}
            />
          </>
        )}
      </Container>
    </Box>
  );
}
